import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	performGetRequest,
	performPostRequest,
} from '@atlassian/jira-fetch/src/utils/requests.tsx';
import {
	type Space,
	type CreateSpaceRequestParams,
	BlueprintCompleteKey,
	type BlueprintData,
	type CreatedSpace,
	MediaType,
} from './types.tsx';

export async function hasSpaceWithProjectName(projectName: string): Promise<string | undefined> {
	try {
		const api = '/wiki/api/v2/spaces';
		const spaces = await performGetRequest(api);

		const spaceKey: string =
			spaces.results?.find((space: Space) => space.name === projectName)?.key || '';

		return spaceKey;
	} catch {
		fireErrorAnalytics({
			meta: {
				id: 'hasSpaceWithProjectName',
				packageName: 'jiraIssueViewBase',

				teamName: 'starburst',
			},
		});
		throw new Error('Failed to fetch Confluence spaces');
	}
}

export async function getBlankSpaceBlueprintId(): Promise<string | undefined> {
	try {
		const api = '/wiki/rest/create-dialog/1.0/space-blueprint/dialog/web-items';
		const spaceBlueprints = await performGetRequest(api);

		const blankSpaceBlueprintId: string =
			spaceBlueprints.find(
				(spaceBlueprint: BlueprintData) =>
					spaceBlueprint.blueprintModuleCompleteKey === BlueprintCompleteKey.BLANK_SPACE,
			)?.contentBlueprintId || '';

		return blankSpaceBlueprintId;
	} catch {
		fireErrorAnalytics({
			meta: {
				id: 'getBlankSpaceBlueprintId',
				packageName: 'jiraIssueViewBase',

				teamName: 'starburst',
			},
		});
		throw new Error('Failed to get blank space blueprintId');
	}
}

// 'Knowledge base' becomes 'KB', and 'Legal' becomes 'LEGAL', etc.
export function spaceNameToSpaceKey(name?: string | null): string | null {
	if (!name) return null;

	const words = name
		.split(' ')
		.map((word) => word.replace(/[^a-zA-Z0-9]+/g, ''))
		.filter((word) => word);

	if (!words) return null;

	let key;
	if (words.length > 1) {
		const initials = words.map((word) => word[0]);
		key = initials.join('').toUpperCase();
	} else {
		key = words[0];
	}

	if (!key) return null;

	return key.length > 10 ? key.substring(0, 10) : key;
}

// Input 'KB' returns 'KB1' or 'KB2' or next available iteration.
// Should return something like 'KB3' or 'LEGAL2' etc.
export async function generateSpaceKey(spaceName: string): Promise<string | undefined> {
	if (!spaceName) {
		return '';
	}

	try {
		const api = `/wiki/rest/onboarding/1/space/generate?key=${spaceName}`;
		const { key } = await performGetRequest(api);

		// fire track event for successful key generation, implementing in STAR-1248

		return key;
	} catch {
		fireErrorAnalytics({
			meta: {
				id: 'generateSpaceKey',
				packageName: 'jiraIssueViewBase',

				teamName: 'starburst',
			},
		});
		throw new Error('Failed to generate a space key');
	}
}

export async function createSpace({
	spaceName,
	spaceKey,
	spaceBlueprintId,
}: CreateSpaceRequestParams): Promise<CreatedSpace | undefined> {
	try {
		const api = '/wiki/rest/create-dialog/1.0/space-blueprint/create-space?favorite=true';
		const data = await performPostRequest(api, {
			body: JSON.stringify({
				context: {
					ContentPageTitle: spaceName,
					members: '',
				},
				name: spaceName,
				spaceKey,
				spaceBlueprintId,
				description: '',
			}),
			headers: {
				'Content-Type': MediaType.JSON,
				Accept: MediaType.JSON,
			},
		});

		// fire track event for successful space creation, implementing in STAR-1248

		return data;
	} catch {
		fireErrorAnalytics({
			meta: {
				id: 'createSpace',
				packageName: 'jiraIssueViewBase',

				teamName: 'starburst',
			},
		});
		throw new Error('Failed to create a JSW project name Confluence space');
	}
}
